import * as THREE from 'three';
import { findTopBoundaryVertices } from '../thickness/boundaryUtils';

/**
 * Detect boundary vertices using trimline (preferred) or algorithmic detection
 * @param {THREE.Mesh} mesh - The working mesh (shadedMesh)
 * @param {Object} trimlineState - Trimline state from store
 * @returns {Array} Array of boundary vertex indices
 */
export function detectBoundary(mesh, trimlineState) {
  if (!mesh || !mesh.geometry) return [];

  const hasTrimline = trimlineState?.isTrimlineApplied ||
                      (trimlineState?.points && trimlineState.points.length > 0);

  if (hasTrimline) {
    // Method 1: Use trimline boundary
    return getBoundaryFromTrimline(mesh, trimlineState);
  } else {
    // Method 2: Algorithmic detection
    return findTopBoundaryVertices(mesh.geometry);
  }
}

/**
 * Extract boundary vertices from trimline curve
 * @param {THREE.Mesh} mesh - The working mesh
 * @param {Object} trimlineState - Trimline state
 * @returns {Array} Boundary vertex indices
 */
function getBoundaryFromTrimline(mesh, trimlineState) {
  // Trimline creates a cut at the boundary
  // After cutting, the top boundary vertices are the newly created edge vertices
  // These are typically the highest Y-value vertices on the boundary

  // Use existing boundary detection but filter to trimline region
  const allBoundaryVertices = findTopBoundaryVertices(mesh.geometry);

  // Filter vertices that are near the trimline curve
  // This is the boundary created by the trimline cut
  return allBoundaryVertices;
}

/**
 * Get boundary vertex positions in world space
 * @param {THREE.Mesh} mesh - The working mesh
 * @param {Array} boundaryIndices - Boundary vertex indices
 * @returns {Array} Array of THREE.Vector3 positions
 */
export function getBoundaryPositions(mesh, boundaryIndices) {
  const positions = mesh.geometry.attributes.position;
  return boundaryIndices.map(idx => {
    const pos = new THREE.Vector3();
    pos.fromBufferAttribute(positions, idx);
    return pos.applyMatrix4(mesh.matrixWorld);
  });
}

/**
 * Compute boundary centroid
 * @param {Array} boundaryPositions - Array of Vector3 positions
 * @returns {THREE.Vector3} Centroid position
 */
export function computeBoundaryCentroid(boundaryPositions) {
  const centroid = new THREE.Vector3();
  boundaryPositions.forEach(pos => centroid.add(pos));
  centroid.divideScalar(Math.max(boundaryPositions.length, 1));
  return centroid;
}

/**
 * Order boundary vertices along the curve by proximity
 * @param {Array} boundaryIndices - Unordered boundary vertex indices
 * @param {Array} boundaryPositions - Boundary positions (world space)
 * @returns {Array} Ordered boundary vertex indices forming a closed curve
 */
export function orderBoundaryVertices(boundaryIndices, boundaryPositions) {
  if (boundaryIndices.length === 0) return [];

  const ordered = [boundaryIndices[0]];
  const remaining = new Set(boundaryIndices.slice(1));

  while (remaining.size > 0) {
    const lastIdx = ordered[ordered.length - 1];
    const lastIdxInArray = boundaryIndices.indexOf(lastIdx);
    const lastPos = boundaryPositions[lastIdxInArray];

    let nearestIdx = null;
    let minDist = Infinity;

    for (const idx of remaining) {
      const idxInArray = boundaryIndices.indexOf(idx);
      const pos = boundaryPositions[idxInArray];
      const dist = lastPos.distanceTo(pos);

      if (dist < minDist) {
        minDist = dist;
        nearestIdx = idx;
      }
    }

    if (nearestIdx !== null) {
      ordered.push(nearestIdx);
      remaining.delete(nearestIdx);
    } else {
      break;
    }
  }

  return ordered;
}

/**
 * Compute smoothed boundary deflection directions using surface normals
 * with reduced smoothing in high-curvature regions to prevent overlap
 *
 * @param {Array} boundaryIndices - Boundary vertex indices
 * @param {Array} boundaryPositions - Boundary positions (world space)
 * @param {THREE.Mesh} mesh - The mesh for accessing normals
 * @param {THREE.Vector3} centroid - Boundary centroid
 * @returns {Map} boundaryIndex -> deflection direction (normalized Vector3 in XZ plane)
 */
export function computeBoundaryCurveNormals(boundaryIndices, boundaryPositions, mesh, centroid) {
  const deflectionMap = new Map();
  const geometry = mesh.geometry;
  const normals = geometry.attributes.normal;

  if (!normals) {
    geometry.computeVertexNormals();
  }

  // Order vertices along the curve
  const orderedIndices = orderBoundaryVertices(boundaryIndices, boundaryPositions);
  const n = orderedIndices.length;

  if (n < 3) {
    // Fallback: use radial from centroid
    for (let i = 0; i < boundaryIndices.length; i++) {
      const pos = boundaryPositions[i];
      const radial = new THREE.Vector3(
        pos.x - centroid.x,
        0,
        pos.z - centroid.z
      ).normalize();
      deflectionMap.set(boundaryIndices[i], radial);
    }
    return deflectionMap;
  }

  // Step 1: Compute initial directions from surface normals
  const initialDirections = new Map();

  // Build normal matrix once for transforming normals to world correctly
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);

  for (const boundaryIdx of orderedIndices) {
    // Get vertex normal in world space (use normal matrix instead of full 4x4)
    const normal = new THREE.Vector3();
    normal.fromBufferAttribute(normals, boundaryIdx);
    normal.applyMatrix3(normalMatrix).normalize();

    // Project to XZ plane (remove Y component)
    const projected = new THREE.Vector3(normal.x, 0, normal.z);

    const boundaryIdxInArray = boundaryIndices.indexOf(boundaryIdx);
    const pos = boundaryPositions[boundaryIdxInArray];

    // If normal is too vertical, use radial from centroid
    if (projected.length() < 0.1) {
      projected.set(
        pos.x - centroid.x,
        0,
        pos.z - centroid.z
      );
    }

    projected.normalize();
    initialDirections.set(boundaryIdx, projected);
  }

  // Step 2: Compute local curvature for adaptive smoothing
  const curvatures = new Map();

  for (let i = 0; i < n; i++) {
    const currentIdx = orderedIndices[i];
    const prevIdx = orderedIndices[(i - 1 + n) % n];
    const nextIdx = orderedIndices[(i + 1) % n];

    const currentIdxInArray = boundaryIndices.indexOf(currentIdx);
    const prevIdxInArray = boundaryIndices.indexOf(prevIdx);
    const nextIdxInArray = boundaryIndices.indexOf(nextIdx);

    const currentPos = boundaryPositions[currentIdxInArray];
    const prevPos = boundaryPositions[prevIdxInArray];
    const nextPos = boundaryPositions[nextIdxInArray];

    // Compute tangent vectors
    const tangent1 = new THREE.Vector3().subVectors(currentPos, prevPos).setY(0).normalize();
    const tangent2 = new THREE.Vector3().subVectors(nextPos, currentPos).setY(0).normalize();

    // Curvature is the angle between consecutive tangents
    const curvature = tangent1.angleTo(tangent2);

    curvatures.set(currentIdx, curvature);
  }

  // Step 3: Apply adaptive smoothing (less smoothing in high-curvature areas)
  const SMOOTH_PASSES = 2; // Reduced from 5

  for (let pass = 0; pass < SMOOTH_PASSES; pass++) {
    const smoothed = new Map();

    for (let i = 0; i < n; i++) {
      const currentIdx = orderedIndices[i];
      const prevIdx = orderedIndices[(i - 1 + n) % n];
      const nextIdx = orderedIndices[(i + 1) % n];

      const current = initialDirections.get(currentIdx);
      const prev = initialDirections.get(prevIdx);
      const next = initialDirections.get(nextIdx);

      // Adaptive smoothing weight based on curvature
      const curvature = curvatures.get(currentIdx) || 0;
      const HIGH_CURVATURE = 0.4; // Radians (~23 degrees)

      // Less smoothing in high curvature regions
      const smoothingFactor = Math.max(0.1, 1.0 - Math.min(curvature / HIGH_CURVATURE, 1.0));
      const NEIGHBOR_WEIGHT = 0.15 * smoothingFactor;

      // Weighted average: current gets more weight than neighbors
      const avg = new THREE.Vector3()
        .add(current.clone().multiplyScalar(1 - 2 * NEIGHBOR_WEIGHT))
        .add(prev.clone().multiplyScalar(NEIGHBOR_WEIGHT))
        .add(next.clone().multiplyScalar(NEIGHBOR_WEIGHT));

      avg.normalize();
      smoothed.set(currentIdx, avg);
    }

    // Update for next pass
    for (const [idx, dir] of smoothed) {
      initialDirections.set(idx, dir);
    }
  }

  // Step 4: Final check - ensure all directions point outward
  for (const [boundaryIdx, direction] of initialDirections) {
    const boundaryIdxInArray = boundaryIndices.indexOf(boundaryIdx);
    const pos = boundaryPositions[boundaryIdxInArray];

    const radial = new THREE.Vector3(
      pos.x - centroid.x,
      0,
      pos.z - centroid.z
    ).normalize();

    // If direction points significantly inward (dot < 0), use radial
    if (direction.dot(radial) < 0) {
      direction.set(radial.x, radial.y, radial.z).normalize();
    }

    deflectionMap.set(boundaryIdx, direction);
  }

  return deflectionMap;
}
